import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

const DelegacionDropDown = ({ selectedObra, setSelectedObra, disabled, idsSeccionesPermitidos }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Normaliza idsSeccionesPermitidos -> [{value,label,color}]
  const raw = Array.isArray(idsSeccionesPermitidos)
    ? idsSeccionesPermitidos
    : idsSeccionesPermitidos
      ? [idsSeccionesPermitidos]
      : [];

  const delegacionOptions = raw
    .map((d, idx) => {
      const id = String(d?.id ?? d?.IdSeccion ?? d?.value ?? '').trim();
      const name = String(d?.name ?? d?.Seccion ?? d?.label ?? '').trim();
      if (!id) return null;
      const short = id.includes('.') ? id.split('.').pop() : id; // "100.1100" -> "1100"
      return {
        key: id || String(idx),
        value: id,
        label: name ? `${short} - ${name}` : short,
        color: '#eee',
      };
    })
    .filter(Boolean);

  // 🔥 Si la obra tiene un valor de Delegacion/Seccion pero no está en las opciones, agregarlo
  const currentVal = selectedObra?.Delegacion ?? selectedObra?.Seccion ?? '';
  const currentOpt = delegacionOptions.find(o => o.value === String(currentVal));
  
  // Si hay un valor actual pero no está en las opciones, agregarlo (para admins viendo obras existentes)
  if (currentVal && !currentOpt) {
    const short = currentVal.includes('.') ? currentVal.split('.').pop() : currentVal;
    delegacionOptions.push({
      key: currentVal,
      value: currentVal,
      label: short,
      color: '#eee',
    });
  }

  const displayLabel =
    currentOpt?.label ||
    (currentVal ? (currentVal.includes('.') ? currentVal.split('.').pop() : currentVal) : '') ||
    (delegacionOptions.length === 1 ? delegacionOptions[0].label : 'Selecciona delegación');

  // Autoselección si solo hay 1 opción
  useEffect(() => {
    if (delegacionOptions.length === 1 && !currentVal) {
      setSelectedObra(prev => ({ ...prev, Delegacion: delegacionOptions[0].value }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegacionOptions.length]);

  // Cerrar al click fuera
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const btnDisabled = disabled || delegacionOptions.length <= 1;

  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Delegación*</label>

      <button
        type="button"
        disabled={btnDisabled}
        onClick={() => setIsOpen(v => !v)}
        className={`DropDownName w-full p-2 border rounded shadow-sm bg-white text-left flex justify-between items-center ${
          btnDisabled ? 'opacity-60 cursor-not-allowed text-black ' : ''
        }`}
        title={btnDisabled ? 'Delegación preseleccionada' : 'Seleccionar delegación'}
      >
        <span className="truncate">{displayLabel}</span>
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && delegacionOptions.length > 1 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto">
          {delegacionOptions.map((option) => {
            const isSel = String(currentVal) === option.value;
            return (
              <li
                key={option.key}
                onClick={() => {
                  setSelectedObra(prev => ({ ...prev, Delegacion: option.value }));
                  setIsOpen(false);
                }}
                className={classNames('cursor-pointer px-4 py-2 text-sm transition-colors', {
                  'bg-gray-100': isSel,
                })}
                style={{ backgroundColor: isSel ? '#f3f4f6' : 'white' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = option.color)}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = isSel ? '#f3f4f6' : 'white')
                }
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DelegacionDropDown;
